import { useMemo } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { UserEdit01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { Button, ButtonSize, ButtonVariant, Modal } from "shared/components";
import { BaseInput } from "shared/components/forms/BaseInput";
import { tenDigitTelParser } from "shared/components/forms/BaseInput/parsers";
import { useFormTyped } from "shared/hooks";
import { validatePhoneNumberFormat } from "shared/utils";
import { landlordSurveyApi } from "api/landlord-surveys/landlord-surveys";
import { getErrorMessage } from "utils/error";
import { object, string } from "yup";
import { EmployerVerification } from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/income";
import {
  nationalDigits,
  recipientChanged
} from "shared/app/EmployerVerification/supervisorContact";

/**
 * BSCPF-6584. "Edit details" on the report's Employer verification tab.
 *
 * Mirrors EditLandlordContactModal, with one deliberate difference: the supervisor needs
 * only *one* reachable channel, so neither email nor phone is individually required —
 * what is required is that at least one survives the edit. The API enforces the same rule
 * (`supervisor_unreachable`); this check exists so the PM is told before the round trip,
 * not instead of it.
 *
 * Changing the email or phone re-points the request at a different person, so the API
 * clears any answers already given. The warning here is what makes that an informed
 * click rather than a surprise.
 */

type FormValues = {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
};

/**
 * Built per supervisor rather than once, so the phone already on file can be left alone.
 *
 * `validatePhoneNumberFormat` is US-only, but the API accepts anything
 * `Boom::PhoneNumber::International` can normalize. A supervisor stored with a non-US
 * number therefore failed a check the server would have passed — and because the form
 * pre-fills that number, the PM could not even correct a misspelled name without first
 * replacing a phone number that was perfectly valid.
 *
 * An unchanged number is accepted as-is; a number the PM actually types is still held to
 * the US format the input's own `tenDigitTelParser` speaks.
 */
const buildValidationSchema = (initialPhone: string) =>
  object({
    first_name: string(),
    last_name: string(),
    // yup's own matcher rather than a hand-rolled pattern: it skips empty strings, which
    // is what clearing the field looks like, and avoids a backtracking-prone regex.
    email: string().email("Enter a valid email"),
    phone: string().test(
      "is-valid-phone",
      "Enter a valid phone number",
      (value) =>
        !value?.trim() ||
        nationalDigits(value) === nationalDigits(initialPhone) ||
        validatePhoneNumberFormat(value)
    )
  });

const trimmed = (value: string | undefined | null) => (value ?? "").trim();

export const EditSupervisorDetailsModal = ({
  employerVerification,
  onClose,
  onSaved
}: {
  employerVerification: EmployerVerification;
  onClose: () => void;
  onSaved: () => void;
}) => {
  const { supervisor, token } = employerVerification;

  const initialPhone = nationalDigits(supervisor.phone);
  const validationSchema = useMemo(
    () => buildValidationSchema(initialPhone),
    [initialPhone]
  );

  const { values, register, onSubmit } = useFormTyped<FormValues>(
    {
      first_name: supervisor.first_name ?? "",
      last_name: supervisor.last_name ?? "",
      email: supervisor.email ?? "",
      phone: initialPhone
    },
    validationSchema
  );

  const { mutate: save, isPending } = useMutation({
    mutationKey: ["EMPLOYER_VERIFICATION_EDIT", token],
    mutationFn: (payload: FormValues) =>
      landlordSurveyApi.updateSupervisorDetails(token, payload),
    onSuccess: () => {
      toast.success("Supervisor details updated and the request was re-sent");
      onSaved();
      onClose();
    },
    onError: (error) =>
      toast.error(
        `Could not update supervisor details: ${getErrorMessage(error)}`
      )
  });

  const hasChannel = Boolean(trimmed(values.email) || trimmed(values.phone));
  // Only a changed email or phone moves the request to a different person; correcting a
  // name does not, and does not cost the answers. The comparison itself is the API's —
  // see supervisorContact.
  const recipientChanges = recipientChanged(supervisor, values);
  const hasAnswers = employerVerification.answers.length > 0;

  return (
    <Modal opened onClose={onClose} panelClassName="w-[366px] p-6">
      <form
        className="space-y-4"
        onSubmit={onSubmit({ onSuccess: (formValues) => save(formValues) })}
      >
        <div className="rounded-full bg-alpha-40 size-16 mx-auto flex items-center justify-center">
          <HugeiconsIcon
            icon={UserEdit01Icon}
            size={32}
            className="text-content-400"
          />
        </div>
        <h3 className="text-lg font-bold text-center">
          Edit supervisor details
        </h3>
        <p className="text-sm text-content-400 text-center">
          Saving re-sends the verification request to the supervisor.
        </p>

        <BaseInput
          id="supervisor_first_name"
          label="First name"
          hideOptional
          placeholder="Dana"
          {...register("first_name")}
        />
        <BaseInput
          id="supervisor_last_name"
          label="Last name"
          hideOptional
          placeholder="Reyes"
          {...register("last_name")}
        />
        <BaseInput
          id="supervisor_email"
          label="Email"
          hideOptional
          placeholder="dana@lonestarbuilders.com"
          {...register("email")}
        />
        <BaseInput
          id="supervisor_phone"
          label="Phone"
          hideOptional
          placeholder="(123) 000-0000"
          parser={tenDigitTelParser({ asString: true })}
          {...register("phone")}
        />

        {!hasChannel ? (
          <p className="rounded-lg bg-danger-50 p-3 text-sm text-danger-800">
            Add an email or a phone number — we need at least one way to reach
            the supervisor.
          </p>
        ) : null}

        {hasChannel && recipientChanges && hasAnswers ? (
          <p className="rounded-lg bg-warning-50 p-3 text-sm text-warning-800">
            Changing the email or phone sends this request to a different
            person, so the answers already given are cleared and the
            verification goes back to waiting.
          </p>
        ) : null}

        <div className="flex gap-2 w-full pt-2">
          <Button
            size={ButtonSize.BASE}
            variant={ButtonVariant.BASE}
            className="w-full"
            onClick={onClose}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            size={ButtonSize.BASE}
            variant={ButtonVariant.PRIMARY}
            className="w-full"
            type="submit"
            loading={isPending}
            disabled={isPending || !hasChannel}
          >
            Save and resend
          </Button>
        </div>
      </form>
    </Modal>
  );
};
